import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    fetchMessages, sendMessage, markThreadRead, announceTyping,
    getTypingUsers, fileToBase64Payload
} from './ChatAPI';

const POLL_MS = 4000;
const TYPING_DEBOUNCE_MS = 2500;
const MAX_FILE_MB = 10;

const formatBubbleTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ', 'T'));
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const ChatWindow = ({ thread, onBack, onRefreshList, onRefreshUnread }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [input, setInput]       = useState('');
    const [sending, setSending]   = useState(false);
    const [typingUsers, setTypingUsers] = useState([]);
    const [pendingFile, setPendingFile] = useState(null);

    const me = parseInt(localStorage.getItem('id') || localStorage.getItem('user_id') || '0', 10);

    const bodyRef = useRef(null);
    const inputRef = useRef(null);
    const fileRef = useRef(null);
    const typingTimer = useRef(null);
    const lastTypingSent = useRef(0);

    // -------- Load messages whenever thread changes --------
    const loadMessages = useCallback(async (showLoader = false) => {
        if (!thread) return;
        if (showLoader) setLoading(true);
        try {
            const res = await fetchMessages(thread.thread_id);
            if (res.success) setMessages(res.data);
        } catch (err) {
            console.warn('loadMessages error:', err);
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [thread]);

    useEffect(() => {
        if (!thread) { setMessages([]); return; }
        loadMessages(true);
        markThreadRead(thread.thread_id).then(onRefreshUnread).catch(() => {});
    }, [thread?.thread_id]);   // eslint-disable-line react-hooks/exhaustive-deps

    // -------- Poll active thread --------
    useEffect(() => {
        if (!thread) return;
        const t = setInterval(() => {
            loadMessages(false);
            getTypingUsers(thread.thread_id).then(r => {
                if (r?.success) setTypingUsers(r.typing || []);
            }).catch(() => {});
        }, POLL_MS);
        return () => clearInterval(t);
    }, [thread?.thread_id, loadMessages]);

    // -------- Auto-scroll on new messages --------
    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages.length]);

    // -------- Reset composer when switching thread --------
    useEffect(() => {
        setInput('');
        setPendingFile(null);
        setTypingUsers([]);
    }, [thread?.thread_id]);

    // -------- Typing announce (debounced) --------
    const handleInputChange = (e) => {
        setInput(e.target.value);
        if (!thread) return;
        const now = Date.now();
        if (now - lastTypingSent.current > TYPING_DEBOUNCE_MS) {
            lastTypingSent.current = now;
            announceTyping(thread.thread_id);
        }
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => {
            lastTypingSent.current = 0;
        }, 4000);
    };

    // -------- File picker --------
    const handleFilePick = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            alert(`File too large — maximum ${MAX_FILE_MB} MB`);
            e.target.value = '';
            return;
        }
        const ok = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'].includes(file.type);
        if (!ok) {
            alert('Only images (JPG/PNG/GIF/WEBP) and PDFs are supported');
            e.target.value = '';
            return;
        }
        setPendingFile(file);
        e.target.value = '';
    };

    // -------- Send --------
    const handleSend = async () => {
        if (!thread || sending) return;
        const text = input.trim();
        if (!text && !pendingFile) return;

        setSending(true);
        try {
            let attachmentPayload = null;
            if (pendingFile) {
                attachmentPayload = await fileToBase64Payload(pendingFile);
            }

            const res = await sendMessage({
                recipient_ids: thread.members.filter(m => m.id !== me).map(m => m.id),
                body: text,
                thread_type: thread.thread_type,
                title: thread.thread_type === 'group' ? thread.title : '',
                attachment: attachmentPayload
            });

            if (res.success) {
                setInput('');
                setPendingFile(null);
                await loadMessages(false);
                onRefreshList?.();
                if (inputRef.current) inputRef.current.focus();
            } else {
                alert(res.error || 'Failed to send');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // -------- Empty state --------
    if (!thread) {
        return (
            <section className="skc-chat-window is-empty">
                <div className="skc-chat-empty-inner">
                    <div className="skc-chat-empty-icon">💬</div>
                    <h3>Your messages</h3>
                    <p>Select a conversation or start a new one.</p>
                </div>
            </section>
        );
    }

    const other = thread.thread_type === 'dm'
        ? thread.members.find(m => m.id !== me)
        : null;
    const title = thread.title || other?.name || 'Conversation';

    return (
        <section className="skc-chat-window">
            {/* Header */}
            <header className="skc-chat-window-head">
                <button className="skc-chat-back" onClick={onBack} aria-label="Back">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <div className="skc-chat-window-head-info">
                    <strong>{title}</strong>
                    <span className="skc-chat-window-head-sub">
                        {thread.thread_type === 'group'
                            ? `${thread.members.length} members`
                            : other?.usertype || 'user'}
                    </span>
                </div>
            </header>

            {/* Messages */}
            <div className="skc-chat-messages" ref={bodyRef}>
                {loading && <div className="skc-chat-loading">Loading…</div>}
                {!loading && messages.length === 0 && (
                    <div className="skc-chat-messages-empty">No messages yet — say hi 👋</div>
                )}

                {messages.map((m) => {
                    const mine = m.sender.id === me;
                    return (
                        <div key={m.id} className={`skc-chat-msg ${mine ? 'is-mine' : 'is-theirs'}`}>
                            {!mine && (
                                <div className="skc-chat-msg-avatar">
                                    {m.sender.image
                                        ? <img src={m.sender.image} alt={m.sender.name} />
                                        : <span>{m.sender.name.charAt(0).toUpperCase()}</span>}
                                </div>
                            )}
                            <div className="skc-chat-msg-bubble">
                                {!mine && thread.thread_type === 'group' && (
                                    <div className="skc-chat-msg-sender">{m.sender.name}</div>
                                )}

                                {m.attachment && (
                                    <div className="skc-chat-msg-attach">
                                        {m.attachment.type?.startsWith('image/') ? (
                                            <a href={m.attachment.url} target="_blank" rel="noreferrer">
                                                <img src={m.attachment.url} alt={m.attachment.name} />
                                            </a>
                                        ) : (
                                            <a href={m.attachment.url} target="_blank" rel="noreferrer" className="skc-chat-msg-file">
                                                📎 {m.attachment.name}
                                            </a>
                                        )}
                                    </div>
                                )}

                                {m.body && <div className="skc-chat-msg-body">{m.body}</div>}

                                <div className="skc-chat-msg-time">
                                    {formatBubbleTime(m.created_at)}
                                    {mine && <span className="skc-chat-tick" title="Sent">✓✓</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {typingUsers.length > 0 && (
                    <div className="skc-chat-typing">
                        {typingUsers.map(u => u.name).join(', ')} typing
                        <span className="skc-chat-typing-dots"><i /><i /><i /></span>
                    </div>
                )}
            </div>

            {/* Pending attachment preview */}
            {pendingFile && (
                <div className="skc-chat-pending">
                    <span>📎 {pendingFile.name}</span>
                    <button onClick={() => setPendingFile(null)} aria-label="Remove">×</button>
                </div>
            )}

            {/* Composer */}
            <footer className="skc-chat-composer">
                <button
                    className="skc-chat-attach-btn"
                    onClick={() => fileRef.current?.click()}
                    title="Attach file"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                </button>
                <input
                    type="file"
                    ref={fileRef}
                    style={{ display: 'none' }}
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleFilePick}
                />
                <textarea
                    ref={inputRef}
                    className="skc-chat-input"
                    placeholder="Type a message…"
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                />
                <button
                    className="skc-chat-send-btn"
                    onClick={handleSend}
                    disabled={sending || (!input.trim() && !pendingFile)}
                >
                    {sending ? '…' : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
                        </svg>
                    )}
                </button>
            </footer>
        </section>
    );
};

export default ChatWindow;